/**
 * Clause Revision API Service
 *
 * Handles clause revision CRUD, AI redraft generation, and change manifest.
 * Task 241: Clause Revision System
 */

import { apiClient } from './client';

// ── Types ───────────────────────────────────────────────────────

export type RevisionSourceType =
  | 'review_fix' | 'compare_align' | 'negotiation_response'
  | 'chat_redraft' | 'uplift_improvement' | 'compliance_fix'
  | 'tracked_change_import' | 'playbook_position' | 'user_edit'
  // Plan 152: align operation source types
  | 'workbench_align' | 'playbook_align';

export type AlignTargetSourceType = 'workbench' | 'playbook' | 'compare_finding';
export type AlignPreset = 'substantive' | 'verbatim';

export type RevisionChangeType = 'replacement' | 'insertion';
export type RevisionNoteType = 'internal' | 'external';
export type RevisionStatus = 'proposed' | 'accepted' | 'rejected';

// Plan 175 — surface discriminator for iterative redraft threads.
// Distinguishes RedraftView-driven and chat-thread-driven redrafts that
// otherwise share (clause_id, source_issue_id|source_finding_id).
export type RedraftSurface = 'redraftview' | 'chat';

// Plan 175 — fields written into the existing source_metadata JSONB column.
// Other source types (align, negotiation) may write different keys; consumers
// must treat unknown keys as absent.
export interface RedraftThreadMetadata {
  redraft_thread_id?: string;
  redraft_surface?: RedraftSurface;
  prior_revision_id?: string;
  /**
   * Plan 35 — server-issued id of the chat_messages row that generated this
   * redraft (= response.assistant_message.id at create time). Acts as a stable
   * join key on hydration so we can re-attach Accept/Reject UI to the right
   * message without depending on positional matching or chronological windows.
   * Only written by chat-surface redrafts; absent on `redraftview` rows and on
   * pre-Plan-35 rows persisted before this field existed.
   */
  chat_assistant_message_id?: string;
}

export interface ClauseRevision {
  id: string;
  document_id: string;
  clause_id: string | null;
  change_type: RevisionChangeType;
  insert_after_clause_id?: string | null;
  source_type: RevisionSourceType;
  source_project_id?: string;
  source_issue_id?: string;
  source_finding_id?: string;
  original_text: string | null;
  revised_text: string;
  revision_prompt?: string;
  status: RevisionStatus;
  version: number;
  author: string;
  note_text?: string;
  note_type?: RevisionNoteType;
  negotiation_round?: number;
  response_group_id?: string;
  external_author?: string;
  external_timestamp?: string;
  // Plan 175 — present on rows produced by iterative redraft. Older rows
  // and other source types may omit it.
  source_metadata?: RedraftThreadMetadata | Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface AnchorTriplet {
  before: string;
  target: string;
  after: string;
  occurrence_hint: number;
  mid_snippet?: string;
  end_snippet?: string;
}

export interface ClauseSignature {
  heading: string;
  first_tokens_normalized: string;
  last_tokens_normalized: string;
  paragraph_index: number | null;
  clause_fingerprint: string;
}

export interface ChangeManifestEntry {
  revision_id: string;
  clause_id: string | null;
  clause_title: string | null;
  change_type: RevisionChangeType;
  original_text: string | null;
  revised_text: string;
  source_type: RevisionSourceType;
  author: string;
  note_text?: string;
  note_type?: RevisionNoteType;
  clause_position: {
    page_start?: number;
    char_start?: number;
    logical_id?: string;
    insert_after_logical_id?: string | null;
  };
  // Plan 130: insertion placement state
  placement?: 'placed' | 'unplaced' | 'start_of_document';
  // Negotiation enrichment (present when analysis_run_id provided)
  anchor?: AnchorTriplet;
  clause_signature?: ClauseSignature;
  response_strength?: string;
}

export interface GenerateRedraftParams {
  source_type: RevisionSourceType;
  source_project_id?: string;
  source_issue_id?: string;
  source_finding_id?: string;
  title?: string;
  description?: string;
  risk_level?: string;
  risk_analysis?: string;
  recommendation?: string;
  // Fallback clause resolution (when clause_id not directly available)
  document_id?: string;
  chunk_index?: number;
  clause_type?: string;  // e.g. "indemnification" — for multi-clause chunk matching
  // Plan 152: Align operation. Required when source_type ends in '_align'.
  // The server loads + ownership-checks the target by (target_source_type,
  // target_source_id) and feeds its own decrypted copy to the LLM. Clients
  // MUST NOT send target_clause_text — anything supplied is ignored.
  target_source_type?: AlignTargetSourceType;
  target_source_id?: string;
  align_preset?: AlignPreset;
  user_instructions?: string;
  matter_id?: string;
  // Plan 175 — iterative redraft thread. New redraft callers MUST send
  // redraft_thread_id and redraft_surface. prior_revision_id is null on v1
  // and set to the previous turn's revision id on every subsequent refine.
  prior_revision_id?: string;
  redraft_thread_id?: string;
  redraft_surface?: RedraftSurface;
}

export interface CreateRevisionParams {
  revised_text: string;
  source_type: RevisionSourceType;
  change_type?: RevisionChangeType;
  clause_id?: string;
  insert_after_clause_id?: string;
  source_project_id?: string;
  source_issue_id?: string;
  source_finding_id?: string;
  revision_prompt?: string;
  author?: string;
  note_text?: string;
  note_type?: RevisionNoteType;
  negotiation_round?: number;
  response_group_id?: string;
  external_author?: string;
  external_timestamp?: string;
  // Plan 175 — chat-thread persist-on-generate stamps {redraft_thread_id,
  // redraft_surface: 'chat', prior_revision_id} into this.
  source_metadata?: RedraftThreadMetadata | Record<string, any>;
}

export interface UpdateRevisionParams {
  status?: RevisionStatus;
  note_text?: string;
  note_type?: RevisionNoteType;
}

export interface DocumentRevisionsResponse {
  revisions: ClauseRevision[];
  total_count: number;
}

export interface ClauseRevisionsResponse {
  revisions: ClauseRevision[];
}

export interface ChangeManifestResponse {
  document_id: string;
  total_changes: number;
  changes: ChangeManifestEntry[];
  // Negotiation enrichment (present when analysis_run_id provided)
  analysis_run_id?: string;
  accepted_as_is?: string[];
  total_clauses_with_findings?: number;
  total_decisions?: number;
  total_apply?: number;
  total_accepted_as_is?: number;
  total_skipped?: number;
  total_pending?: number;
}

// ── API Class ───────────────────────────────────────────────────

class ClauseRevisionAPI {
  async generateRedraft(
    clauseId: string,
    params: GenerateRedraftParams
  ): Promise<{ revision_id: string; revised_text: string; status: string }> {
    const response = await apiClient.post(
      `/api/v1/clauses/${clauseId}/revisions/generate`,
      params
    );
    return response.data;
  }

  async createRevision(
    documentId: string,
    data: CreateRevisionParams
  ): Promise<ClauseRevision> {
    const response = await apiClient.post<ClauseRevision>(
      `/api/v1/documents/${documentId}/revisions`,
      data
    );
    return response.data;
  }

  async reanchorRevision(
    documentId: string,
    revisionId: string,
    insertAfterClauseId: string | null,
  ): Promise<ClauseRevision> {
    // Plan 130: insertAfterClauseId is written verbatim. `null` → start of
    // document. The "__start__" literal is a client-only map key and MUST NOT
    // be sent on the wire (backend rejects with 400).
    const response = await apiClient.patch<ClauseRevision>(
      `/api/v1/documents/${documentId}/revisions/${revisionId}/reanchor`,
      { insert_after_clause_id: insertAfterClauseId }
    );
    return response.data;
  }

  async updateRevision(
    revisionId: string,
    data: UpdateRevisionParams
  ): Promise<ClauseRevision> {
    const response = await apiClient.patch<ClauseRevision>(
      `/api/v1/revisions/${revisionId}`,
      data
    );
    return response.data;
  }

  async getDocumentRevisions(
    documentId: string,
    filters?: {
      status?: string[];
      change_type?: RevisionChangeType;
      negotiation_round?: number;
    }
  ): Promise<DocumentRevisionsResponse> {
    const params = new URLSearchParams();
    if (filters?.status) {
      filters.status.forEach(s => params.append('status', s));
    }
    if (filters?.change_type) {
      params.set('change_type', filters.change_type);
    }
    if (filters?.negotiation_round !== undefined) {
      params.set('negotiation_round', String(filters.negotiation_round));
    }
    const query = params.toString();
    const url = `/api/v1/documents/${documentId}/revisions${query ? `?${query}` : ''}`;
    const response = await apiClient.get<DocumentRevisionsResponse>(url);
    return response.data;
  }

  async getClauseRevisions(clauseId: string): Promise<ClauseRevisionsResponse> {
    const response = await apiClient.get<ClauseRevisionsResponse>(
      `/api/v1/clauses/${clauseId}/revisions`
    );
    return response.data;
  }

  async getResponseGroupRevisions(responseGroupId: string): Promise<ClauseRevisionsResponse> {
    const response = await apiClient.get<ClauseRevisionsResponse>(
      `/api/v1/revisions/group/${responseGroupId}`
    );
    return response.data;
  }

  async createManualRevision(
    documentId: string,
    clauseId: string,
    data: { proposed_text: string; client_current_revision_id?: string }
  ): Promise<ClauseRevision> {
    const response = await apiClient.post<ClauseRevision>(
      `/api/v1/documents/${documentId}/clauses/${clauseId}/manual-revision`,
      data
    );
    return response.data;
  }

  async getChangeManifest(documentId: string, analysisRunId?: string): Promise<ChangeManifestResponse> {
    const params = analysisRunId ? `?analysis_run_id=${encodeURIComponent(analysisRunId)}` : '';
    const response = await apiClient.get<ChangeManifestResponse>(
      `/api/v1/documents/${documentId}/change-manifest${params}`
    );
    return response.data;
  }
}

export const clauseRevisionApi = new ClauseRevisionAPI();
